import { IoIosSearch } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import React, { useState, useEffect, useRef } from "react";
import "./Table.css";
import { useNavigate } from "react-router-dom";
import { CiEdit } from "react-icons/ci";
import Pagination from "../Components/Pagination";
import { FiFilter } from "react-icons/fi";
import { PiSortAscendingThin } from "react-icons/pi";
import axiosInstance from "../axiosinstance";
import {
  formatBadgeLabel,
  getPaymentModeBadgeClass,
} from "../helperFunctions";

function RecordPayement({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [Records, setRecords] = useState([]); // State to hold records
  const [searchVisible, setSearchVisible] = useState(false); // State for toggling search bar
  const [searchQuery, setSearchQuery] = useState(""); // State to hold search query
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [totalRecords, settotalRecords] = useState();

  const handleNewRecord = () => {
    navigate("/record-payement-form");
  };
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const containerRef = useRef();
  const ITEMS_PER_PAGE = 100;
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);

  function formatDate(dateString) {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
    };

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", options);
  }

  // const fetchPaymentRecords = async (status = "Active", currentPage = 1) => {
  //   try {
  //     const authKey = localStorage.getItem("authKey");

  //     if (!authKey) {
  //       console.error("No auth key found in localStorage");
  //       return;
  //     }

  //     const response = await axios.get(`${API_URL}/api/get_recorded_payment`, {
  //       headers: {
  //         Authorization: `Bearer ${authKey}`,
  //       },
  //       params: {
  //         page: Math.max(1, currentPage), // Ensure at least page 1
  //         per_page: ITEMS_PER_PAGE, // Fetch only 5 records per page
  //       },
  //     });

  //     // Update records with data from the response
  //     setRecords(response.data.data); // Ensure we are accessing the correct data
  //     settotalRecords(response.data.Total_records || 0);
  //   } catch (error) {
  //     console.error("Error fetching records:", error);
  //   }
  // };

  const fetchPaymentRecords = async (
    currentPage = 1,
    per_page = ITEMS_PER_PAGE
  ) => {
    try {
      const response = await axiosInstance.get(`/api/get_recorded_payment`, {
        params: {
          page: Math.max(1, currentPage),
          per_page,
        },
      });

      setRecords(response.data.data);
      settotalRecords(response.data.Total_records || 0);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  const fetchSearchedPaymentRecords = async (
    query,
    currentPage = 1,
    per_page = ITEMS_PER_PAGE
  ) => {
    try {
      const response = await axiosInstance.get(`/api/search_recorded_payment`, {
        params: { query, page: Math.max(1, currentPage), per_page },
      });

      let searchResults = response.data.data || [];

      // Keep existing client-side filtering behavior for search results.
      searchResults = searchResults.filter((record) => {
        const searchLower = query.toLowerCase().trim();

        return (
          (record.client_name &&
            record.client_name.toLowerCase().includes(searchLower)) ||
          (record.client_company_name &&
            record.client_company_name.toLowerCase().includes(searchLower)) ||
          (record.payment_mode &&
            record.payment_mode.toLowerCase().includes(searchLower)) ||
          (record.amount && record.amount.toString().includes(searchLower)) ||
          (record.serial_number &&
            record.serial_number.toString().includes(searchLower)) ||
          (record.payment_date &&
            formatDate(record.payment_date).toLowerCase().includes(searchLower))
        );
      });

      setRecords(searchResults);
      settotalRecords(
        response.data.Total_records ??
          response.data.total_records ??
          searchResults.length
      );
    } catch (error) {
      console.error("Error fetching search results:", error.message);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig?.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedRecords = [...Records].sort((a, b) => {
      let aValue = a[key] ?? ""; // Handle null/undefined values
      let bValue = b[key] ?? "";

      // Special handling for date fields
      if (key === "payment_date") {
        aValue = a[key] ? new Date(a[key]) : new Date(0);
        bValue = b[key] ? new Date(b[key]) : new Date(0);
      }

      // Special handling for numeric values
      if (key === "amount") {
        aValue = parseFloat(a[key]) || 0;
        bValue = parseFloat(b[key]) || 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setRecords(sortedRecords);
  };

  // useEffect(() => {
  //   fetchPaymentRecords("Active", page, itemsPerPage); // Ensure correct parameters are passed
  // }, [page, itemsPerPage]); // Fetch data when `page` or `itemsPerPage` changes

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!searchQuery.trim()) {
          await fetchPaymentRecords(page, itemsPerPage);
          return;
        }

        await fetchSearchedPaymentRecords(searchQuery, page, itemsPerPage);
      } catch (error) {
        console.error("Error fetching records:", error);
      }
    };

    fetchData();
  }, [page, itemsPerPage, searchQuery]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsSortPopup(false);
      }
    };

    // Only add the event listener when the popup is open
    if (isSortPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortPopup]);

  const handleEdit = (payment_id) => {
    navigate(`/edit-record-payement/${payment_id}`);
  };

  const handlePaginationChange = (newPage, itemsPerPage) => {
    setPage(newPage);
    setItemsPerPage(itemsPerPage);
  };

  const toggleSortPopup = (event) => {
    event.stopPropagation(); // Prevent event bubbling
    setIsSortPopup(!isSortPopup);
  };

  return (
    <div
      className={`table-container record-payment-page ${isSidebarExpanded ? "expanded" : "collapsed"
        }`}
    >
      <div className="table-container-content record-payment-content">
        <div className="table-header">
          <div className="table-header-content">
            <div className="table-left-side">
              <h2 className="jost-bold">Record Payment</h2>
            </div>
            <div className="table-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  className="search-bar"
                  value={searchQuery}
                  onChange={handleSearch} // Use handleSearch to update search query and fetch data
                />
                <IoIosSearch className="search-bar-icon" />
              </div>
              <div className="new-button" onClick={handleNewRecord}>
                <span className="new-icon">
                  <LuPlus />
                </span>
                <span>New</span>
              </div>

              <button className="record-payment-sort-button" onClick={toggleSortPopup}>
                <span className="action-icon custom-action-icon-size">
                  <PiSortAscendingThin />
                </span>
              </button>

              {isSortPopup && (
                <div className="sort-popup-container" ref={containerRef}>
                  <div className="sort-popup-header">
                    <span className="sort-title">Sort By</span>
                  </div>
                  <div className="sort-popup-content">
                    <button
                      className="sort-button"
                      onClick={() => handleSort("payment_date")}
                    >
                      Payment Date
                    </button>
                    <button
                      className="sort-button"
                      onClick={() => handleSort("client_name")}
                    >
                      Client Name
                    </button>
                    <button
                      className="sort-button"
                      onClick={() => handleSort("amount")}
                    >
                      Amount
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="data-list-body record-payment-data-body">
          <div className="data-list-scroll-body record-payment-scroll-body">
            {/* <div className="data-list-table"> */}
              <table className="table multilevel-data df-txt-left record-payment-table">
                <thead className="sticky-header">
                  <tr className="table-row">
                    <th>Sr. No</th>
                    <th>Payment Date</th>
                    <th> <span style={{
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}>Client Name</span></th>
                    <th> <span style={{
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}>Client Company Name</span></th>
                    <th className="Payement-type price-left-align">Payment Type</th>
                    <th className="price-left-align">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(Records) && Records.length > 0 ? (
                    Records.map((record, index) => (
                      <tr className="hoverable-row" key={index}>
                        <td className="center-align payment-table">
                          {record.serial_number}
                        </td>
                        <td className="center-align payment-table">
                          {formatDate(record.payment_date)}
                        </td>
                        <td
                        // className="center-align payment-table"
                        // onClick={() => handleView?.(record.payment_id)}
                        >
                          {record.client_name
                            ? record.client_name
                              .toLowerCase()
                              .split(' ')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                            : "N/A"}
                        </td>
                        <td
                        // className="center-align payment-table"
                        // onClick={() => handleView?.(record.payment_id)}
                        >
                          {record.client_company_name
                            ? record.client_company_name
                              .toLowerCase()
                              .split(' ')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                            : "N/A"}
                        </td>
                        <td>
                          <div
                            className={`value-badge ${getPaymentModeBadgeClass(
                              record.payment_mode
                            )}`}
                          >
                            {formatBadgeLabel(record.payment_mode)}
                          </div>
                        </td>

                        <td className="center-align payment-table price-left-align">
                          {record.amount || "0.00"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data-text">
                        Currently, there are no records to display
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            {/* </div> */}
          </div>
          <Pagination
            className="record-payment-pagination"
            showPerPage={ITEMS_PER_PAGE}
            total={totalRecords}
            onPaginationChange={handlePaginationChange}
          />
        </div>
      </div>
    </div>
  );
}

export default RecordPayement;
